import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import DataTable from "../components/DataTable";
import PageHeader from "../components/PageHeader";
import { Badge, statusVariant } from "../components/Badge";
import { Trash2, Wallet, KeyRound, Ban, CheckCircle2, History, Percent, ShieldOff, Pencil, Mail, MailCheck, Coins } from "lucide-react";
import { toast } from "sonner";
import { fmtBDT } from "../lib/format";
import NumberInput from "../../components/ui/NumberInput";

export default function Agents() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjustModal, setAdjustModal] = useState(null);
  const [commissionModal, setCommissionModal] = useState(null);
  const [pwdModal, setPwdModal] = useState(null);
  const [commModal, setCommModal] = useState(null);
  const [editModal, setEditModal] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/agents");
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (row) => {
    if (!window.confirm(`Delete agent ${row.username || row.name}? This will not undo their past deposits/withdrawals.`)) return;
    try {
      await api.delete(`/agents/${row.id}`);
      toast.success("Agent removed");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "string") {
        if (window.confirm(`${detail}\n\nForce delete anyway?`)) {
          try {
            await api.delete(`/agents/${row.id}?force=true`);
            toast.success("Agent force-deleted");
            load();
          } catch (err2) {
            toast.error(err2?.response?.data?.detail || "Force delete failed");
          }
        }
      } else {
        toast.error("Delete failed");
      }
    }
  };

  const toggleBlock = async (row) => {
    const isBlocked = row.status !== "active";
    const action = isBlocked ? "unblock" : "block";
    if (!window.confirm(`${isBlocked ? "Unblock" : "Block"} agent ${row.username}? ${isBlocked ? "They will regain panel access." : "They will be unable to log in to the Agent Panel."}`)) return;
    try {
      await api.post(`/agents/${row.id}/${action}`);
      toast.success(`Agent ${isBlocked ? "unblocked" : "blocked"}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const view30Days = (row) => {
    const to = new Date().toISOString().slice(0, 10);
    const from = new Date(Date.now() - 29 * 86400_000).toISOString().slice(0, 10);
    // Only successful deposits/withdrawals by default — admin can clear the
    // status filter on the destination page to see pending/rejected too.
    navigate(`/root/admin/agents/transactions?agent_id=${row.id}&date_from=${from}&date_to=${to}&status=approved`);
  };

  const adjustBalance = async (row, delta, note) => {
    try {
      const { data } = await api.post(`/agents/${row.id}/adjust-balance`, { amount: delta, note });
      toast.success(`Agent balance updated → ৳${data.balance.toFixed(2)}`);
      setAdjustModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const creditCommission = async (row, payload) => {
    try {
      const { data } = await api.post(`/agents/${row.id}/adjust-balance`, {
        amount: Number(payload.amount),
        kind: "commission",
        period_label: payload.period_label,
        monthly_turnover: Number(payload.monthly_turnover || 0),
        commission_pct: Number(payload.commission_pct || 3),
        note: payload.note || "",
      });
      toast.success(
        `Commission ৳${Number(payload.amount).toFixed(2)} credited → new balance ৳${data.balance.toFixed(2)}${data.email_sent ? " · ✉ email sent" : ""}`,
        { duration: 5500 }
      );
      setCommissionModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const resetPassword = async (row, newPassword) => {
    try {
      await api.post(`/agents/${row.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${row.username}`);
      setPwdModal(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const reset2FA = async (row) => {
    if (!window.confirm(`Disable Google Authenticator (2FA) for agent ${row.username}?\nUse only on emergency requests when the agent lost their authenticator.`)) return;
    try {
      await api.post(`/site/admin/agents/${row.id}/2fa/reset`);
      toast.success(`2FA disabled for ${row.username}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reset 2FA");
    }
  };

  const resendWelcomeEmail = async (row) => {
    const ok = window.confirm(
      `Resend the welcome email to ${row.email}?\n\n` +
      `• আপডেটেড Agent details সহ (username, commission, address etc.)\n` +
      `• Operating Handbook PDF attached\n` +
      `• ❌ Password পরিবর্তন হবে না — Agent আগের password দিয়েই login করবে\n\n` +
      `Continue?`
    );
    if (!ok) return;
    const resetPw = window.confirm(
      `[Optional] Agent কি password ভুলে গেছেন?\n\n` +
      `OK = একটি নতুন one-time password generate করে email-এ পাঠাবো (পুরনো password invalid হবে)\n` +
      `Cancel = পুরনো password রেখে শুধু updated details email করবো (recommended)`
    );
    try {
      const { data } = await api.post(`/agents/${row.id}/resend-welcome`, { reset_password: resetPw });
      toast.success(
        resetPw
          ? `Welcome email queued for ${data.delivered_to} · NEW password generated`
          : `Updated details email queued for ${data.delivered_to} · password unchanged`,
        { duration: 5500 }
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    }
  };

  const saveCommissions = async (row, payload) => {
    try {
      await api.patch(`/agents/${row.id}`, payload);
      toast.success(`Commissions updated for ${row.username}`);
      setCommModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const saveProfile = async (row, payload, balanceDelta, balanceNote) => {
    try {
      // 1) Update profile fields (username/email/name/mobile/city/state/commissions)
      if (Object.keys(payload).length > 0) {
        await api.patch(`/agents/${row.id}`, payload);
      }
      // 2) Apply balance delta (if any) via the audited adjust-balance endpoint
      if (Math.abs(Number(balanceDelta || 0)) > 0.0001) {
        await api.post(`/agents/${row.id}/adjust-balance`, {
          amount: Number(balanceDelta),
          note: balanceNote || `Profile edit by admin`,
        });
      }
      toast.success(`Agent ${payload.username || row.username} updated`);
      setEditModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const failedCount = rows.filter((r) => r.welcome_email_status === "failed").length;
  const firstFailedErr = (rows.find((r) => r.welcome_email_status === "failed") || {}).welcome_email_error;

  return (
    <div data-testid="agents-page">
      <PageHeader title="Agent List" subtitle="All partner agents — they can log in at /agent/login to deposit funds & approve user withdrawals" />

      {failedCount > 0 && (
        <div
          className="mb-4 rounded-xl border border-rose-200 bg-gradient-to-r from-rose-50 to-amber-50 p-4 flex items-start gap-3"
          data-testid="agents-email-failed-banner"
        >
          <div className="h-9 w-9 rounded-lg bg-rose-100 grid place-items-center text-rose-700 flex-shrink-0">
            <Mail size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-rose-800">
              {failedCount} agent{failedCount === 1 ? "" : "s"} welcome email failed to deliver
            </div>
            {firstFailedErr && (
              <div className="text-xs text-rose-700/80 mt-0.5 font-mono break-words line-clamp-2">
                {firstFailedErr}
              </div>
            )}
            <div className="text-xs text-rose-700 mt-1.5">
              Fix it once: go to{" "}
              <a href="/root/admin/settings/email" className="font-bold underline hover:text-rose-900">
                Admin → Settings → Email Gateway
              </a>{" "}
              and click <b>Send Test</b> to verify your SMTP credentials. Then click ✉ on each failed row to retry.
            </div>
          </div>
        </div>
      )}

      <DataTable
        testId="agents-table"
        loading={loading}
        rows={rows}
        columns={[
          { key: "username", label: "Username", render: (r) => (
            <div className="flex items-center gap-2">
              <span className="font-mono text-emerald-700">{r.username || "—"}</span>
              {r.parent_agent_username && (
                <span className="text-[10px] uppercase tracking-wider bg-purple-100 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded-sm font-semibold" title={`Sub-agent of ${r.parent_agent_username}`}>
                  Sub · {r.parent_agent_username}
                </span>
              )}
            </div>
          ) },
          { key: "name", label: "Name" },
          { key: "city", label: "City", render: (r) => r.city || <span className="text-gray-400">—</span> },
          { key: "state", label: "State (Address)", render: (r) => <span className="text-[12px]" title={r.state}>{r.state || <span className="text-gray-400">—</span>}</span> },
          { key: "mobile", label: "Mobile" },
          { key: "deposit_commission_pct", label: "Dep %", align: "right", render: (r) => `${Number(r.deposit_commission_pct || 0).toFixed(1)}%` },
          { key: "withdraw_commission_pct", label: "Wd %", align: "right", render: (r) => `${Number(r.withdraw_commission_pct || 0).toFixed(1)}%` },
          { key: "balance", label: "Balance", align: "right", render: (r) => fmtBDT(r.balance) },
          { key: "status", label: "Status", render: (r) => <Badge variant={statusVariant(r.status)}>{r.status}</Badge> },
          { key: "welcome_email_status", label: "Email", render: (r) => {
            const s = r.welcome_email_status || (r.welcome_email_sent_at ? "delivered" : "");
            const at = r.welcome_email_sent_at || r.welcome_email_queued_at;
            const ts = at ? new Date(at).toLocaleString() : "";
            const err = r.welcome_email_error || "";
            const cfg = {
              delivered: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Delivered" },
              queued:    { cls: "bg-blue-100 text-blue-800 border-blue-200",          label: "Queued"    },
              failed:    { cls: "bg-rose-100 text-rose-800 border-rose-200",          label: "Failed"    },
            }[s] || { cls: "bg-gray-100 text-gray-500 border-gray-200", label: "Not sent" };
            const tip = s === "failed" && err
              ? `Failed${ts ? " · " + ts : ""}\n\n${err}\n\nFix: Admin → Settings → Email Gateway → enter correct SMTP password and click Send Test.`
              : ts ? `${cfg.label} · ${ts}` : "Welcome email never sent — click ✉ to send";
            return (
              <span
                title={tip}
                className={`inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-md border cursor-help ${cfg.cls}`}
                data-testid={`agents-email-status-${r.id}`}
              >
                {s === "delivered" && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />}
                {s === "queued"    && <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />}
                {s === "failed"    && <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />}
                {!s                && <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />}
                {cfg.label}
              </span>
            );
          } },
        ]}
        actions={(row) => (
          <div className="flex items-center gap-1">
            <button onClick={() => setEditModal(row)} title="Edit agent profile (name, username, email, phone, city, state, balance, commissions)" className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-emerald-600 hover:border-emerald-200" data-testid={`agents-edit-${row.id}`}>
              <Pencil size={14} />
            </button>
            <button onClick={() => setAdjustModal(row)} title="Adjust balance" className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-emerald-600 hover:border-emerald-200" data-testid={`agents-adjust-${row.id}`}>
              <Wallet size={14} />
            </button>
            <button onClick={() => setCommissionModal(row)} title="Add monthly commission (sends ultra-pro email from Agent Gateway)" className="h-8 w-8 inline-flex items-center justify-center border border-amber-200 rounded-sm text-amber-600 hover:text-amber-800 hover:bg-amber-50 hover:border-amber-300" data-testid={`agents-commission-${row.id}`}>
              <Coins size={14} />
            </button>
            <button onClick={() => setCommModal(row)} title="Edit commissions" className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-purple-600 hover:border-purple-200" data-testid={`agents-comm-${row.id}`}>
              <Percent size={14} />
            </button>
            <button onClick={() => view30Days(row)} title="View last 30 days transactions" className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-blue-600 hover:border-blue-200" data-testid={`agents-history-${row.id}`}>
              <History size={14} />
            </button>
            <button onClick={() => setPwdModal(row)} title="Reset password" className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-amber-600 hover:border-amber-200" data-testid={`agents-pwd-${row.id}`}>
              <KeyRound size={14} />
            </button>
            <button onClick={() => reset2FA(row)} title="Disable 2FA (Google Authenticator) for this agent" className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-rose-600 hover:border-rose-200" data-testid={`agents-reset-2fa-${row.id}`}>
              <ShieldOff size={14} />
            </button>
            <button
              onClick={() => resendWelcomeEmail(row)}
              title={
                row.welcome_email_sent_at
                  ? `Resend welcome email with current details (password unchanged) · last sent ${new Date(row.welcome_email_sent_at).toLocaleString()}`
                  : "Resend welcome email with current details + Operating Handbook PDF · password stays the same by default"
              }
              className={`h-8 w-8 inline-flex items-center justify-center border rounded-sm transition ${
                row.welcome_email_status === "delivered"
                  ? "border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                  : row.welcome_email_status === "failed"
                  ? "border-rose-200 text-rose-600 hover:bg-rose-50"
                  : "border-gray-200 text-gray-500 hover:text-emerald-700 hover:border-emerald-200"
              }`}
              data-testid={`agents-resend-welcome-${row.id}`}
            >
              {row.welcome_email_status === "delivered" ? <MailCheck size={14} /> : <Mail size={14} />}
            </button>
            <button onClick={() => toggleBlock(row)} title={row.status === "active" ? "Block agent" : "Unblock agent"} className={`h-8 w-8 inline-flex items-center justify-center border rounded-sm ${row.status === "active" ? "border-gray-200 text-gray-500 hover:text-orange-600 hover:border-orange-200" : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"}`} data-testid={`agents-block-${row.id}`}>
              {row.status === "active" ? <Ban size={14} /> : <CheckCircle2 size={14} />}
            </button>
            <button onClick={() => remove(row)} title="Delete" className="h-8 w-8 inline-flex items-center justify-center border border-gray-200 rounded-sm text-gray-500 hover:text-red-600 hover:border-red-200" data-testid={`agents-delete-${row.id}`}>
              <Trash2 size={14} />
            </button>
          </div>
        )}
      />

      {adjustModal && <AdjustModal agent={adjustModal} onClose={() => setAdjustModal(null)} onSubmit={adjustBalance} />}
      {commissionModal && <CommissionCreditModal agent={commissionModal} onClose={() => setCommissionModal(null)} onSubmit={creditCommission} />}
      {pwdModal && <PasswordModal agent={pwdModal} onClose={() => setPwdModal(null)} onSubmit={resetPassword} />}
      {commModal && <CommissionsModal agent={commModal} onClose={() => setCommModal(null)} onSubmit={saveCommissions} />}
      {editModal && <EditProfileModal agent={editModal} onClose={() => setEditModal(null)} onSubmit={saveProfile} />}
    </div>
  );
}

function AdjustModal({ agent, onClose, onSubmit }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const numAmount = parseFloat(amount);
  const isValid = !isNaN(numAmount) && numAmount !== 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-md p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="adjust-balance-modal">
        <h3 className="text-lg font-bold mb-1">Adjust Balance — {agent.username}</h3>
        <p className="text-xs text-gray-500 mb-4">Current balance: ৳{Number(agent.balance).toFixed(2)} • Use negative to debit.</p>
        <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Amount (positive = credit, negative = debit)</label>
        <NumberInput value={amount} onChange={(raw) => setAmount(raw)} asString className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="adjust-balance-amount" />
        <label className="block text-xs uppercase tracking-wider text-gray-500 mt-3 mb-1">Note (optional)</label>
        <input value={note} onChange={(e) => setNote(e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="adjust-balance-note" />
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="h-9 px-4 border border-gray-200 rounded-sm text-sm">Cancel</button>
          <button disabled={busy || !isValid} onClick={async () => { setBusy(true); await onSubmit(agent, numAmount, note); setBusy(false); }} className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold disabled:opacity-50" data-testid="adjust-balance-submit">
            {busy ? "Saving..." : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PasswordModal({ agent, onClose, onSubmit }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-md p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="reset-password-modal">
        <h3 className="text-lg font-bold mb-1">Reset Password — {agent.username}</h3>
        <p className="text-xs text-gray-500 mb-4">Set a new login password for this agent. Share it securely.</p>
        <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">New Password (min 6)</label>
        <input type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="reset-password-input" />
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="h-9 px-4 border border-gray-200 rounded-sm text-sm">Cancel</button>
          <button disabled={busy || pwd.length < 6} onClick={async () => { setBusy(true); await onSubmit(agent, pwd); setBusy(false); }} className="h-9 px-4 bg-amber-500 hover:bg-amber-600 text-white rounded-sm text-sm font-semibold disabled:opacity-50" data-testid="reset-password-submit">
            {busy ? "Saving..." : "Reset"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CommissionsModal({ agent, onClose, onSubmit }) {
  const isSub = !!agent.parent_agent_id;
  const [form, setForm] = useState({
    deposit_commission_pct: Number(agent.deposit_commission_pct || 0),
    withdraw_commission_pct: Number(agent.withdraw_commission_pct || 0),
    parent_deposit_override_pct: Number(agent.parent_deposit_override_pct || 0),
    parent_withdraw_override_pct: Number(agent.parent_withdraw_override_pct || 0),
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: parseFloat(e.target.value) || 0 });

  const submit = async () => {
    setBusy(true);
    const payload = {
      deposit_commission_pct: form.deposit_commission_pct,
      withdraw_commission_pct: form.withdraw_commission_pct,
    };
    if (isSub) {
      payload.parent_deposit_override_pct = form.parent_deposit_override_pct;
      payload.parent_withdraw_override_pct = form.parent_withdraw_override_pct;
    }
    await onSubmit(agent, payload);
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="commissions-modal">
        <h3 className="text-lg font-bold mb-1">Edit Commissions — {agent.username}</h3>
        <p className="text-xs text-gray-500 mb-5">
          {isSub ? <>This is a <strong>sub-agent</strong> of <span className="text-purple-700 font-mono">{agent.parent_agent_username}</span>. Set both their own commission and the parent override.</> : <>Top-level agent — only their own commissions apply.</>}
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Pct label="Own · Deposit %" value={form.deposit_commission_pct} onChange={set("deposit_commission_pct")} testId="comm-own-deposit" />
            <Pct label="Own · Withdraw %" value={form.withdraw_commission_pct} onChange={set("withdraw_commission_pct")} testId="comm-own-withdraw" />
          </div>

          {isSub && (
            <div className="border border-purple-200 bg-purple-50/40 rounded-sm p-4">
              <div className="text-[11px] uppercase tracking-wider text-purple-700 font-bold mb-2">
                Parent Override · paid to <span className="font-mono">{agent.parent_agent_username}</span>
              </div>
              <p className="text-[11px] text-purple-700/80 mb-3 leading-relaxed">
                On every deposit / approved withdrawal made by <strong>{agent.username}</strong>, the parent agent gets the % below credited automatically (in addition to {agent.username}'s own commission).
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Pct label="Parent · Deposit Override %" value={form.parent_deposit_override_pct} onChange={set("parent_deposit_override_pct")} testId="comm-parent-deposit" />
                <Pct label="Parent · Withdraw Override %" value={form.parent_withdraw_override_pct} onChange={set("parent_withdraw_override_pct")} testId="comm-parent-withdraw" />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="h-9 px-4 border border-gray-200 rounded-sm text-sm">Cancel</button>
          <button disabled={busy} onClick={submit} className="h-9 px-4 bg-purple-500 hover:bg-purple-600 text-white rounded-sm text-sm font-semibold disabled:opacity-50" data-testid="commissions-save">
            {busy ? "Saving..." : "Save Commissions"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Pct({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</label>
      <div className="relative">
        <NumberInput min="0" max="100" value={value} onChange={onChange} className="w-full h-10 pl-3 pr-8 border border-gray-200 rounded-sm text-sm tabular" data-testid={testId} />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
      </div>
    </div>
  );
}


function EditProfileModal({ agent, onClose, onSubmit }) {
  const isSub = !!agent.parent_agent_id;
  const [districts, setDistricts] = useState([]);
  const [form, setForm] = useState({
    username: agent.username || "",
    name: agent.name || "",
    email: agent.email || "",
    mobile: agent.mobile || "",
    city: agent.city || "",
    state: agent.state || "",
    deposit_commission_pct: Number(agent.deposit_commission_pct || 0),
    withdraw_commission_pct: Number(agent.withdraw_commission_pct || 0),
    parent_deposit_override_pct: Number(agent.parent_deposit_override_pct || 0),
    parent_withdraw_override_pct: Number(agent.parent_withdraw_override_pct || 0),
    new_balance: Number(agent.balance || 0),
    balance_note: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/bd-districts").then((r) => setDistricts(r.data?.items || [])).catch(() => setDistricts([]));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setNum = (k) => (n) => setForm({ ...form, [k]: n });

  const submit = async () => {
    // Build PATCH payload — only include fields that actually changed (avoids
    // unnecessary uniqueness checks on unchanged username/email).
    const payload = {};
    const trim = (s) => (s ?? "").toString().trim();
    if (trim(form.username) !== trim(agent.username || "")) {
      if (trim(form.username).length < 3 || trim(form.username).length > 32) {
        toast.error("Username must be 3-32 characters"); return;
      }
      payload.username = trim(form.username);
    }
    if (trim(form.name) !== trim(agent.name || "")) payload.name = trim(form.name);
    if (trim(form.email).toLowerCase() !== trim(agent.email || "").toLowerCase()) {
      if (!trim(form.email).includes("@")) { toast.error("Invalid email"); return; }
      payload.email = trim(form.email).toLowerCase();
    }
    if (trim(form.mobile) !== trim(agent.mobile || "")) payload.mobile = trim(form.mobile);
    if (trim(form.city) !== trim(agent.city || "")) {
      if (!trim(form.city)) { toast.error("Select a City (Bangladesh district)"); return; }
      payload.city = trim(form.city);
    }
    if (trim(form.state) !== trim(agent.state || "")) {
      if (!trim(form.state)) { toast.error("State (address) is required"); return; }
      payload.state = trim(form.state);
    }
    if (Number(form.deposit_commission_pct) !== Number(agent.deposit_commission_pct || 0)) {
      payload.deposit_commission_pct = Number(form.deposit_commission_pct);
    }
    if (Number(form.withdraw_commission_pct) !== Number(agent.withdraw_commission_pct || 0)) {
      payload.withdraw_commission_pct = Number(form.withdraw_commission_pct);
    }
    if (isSub) {
      if (Number(form.parent_deposit_override_pct) !== Number(agent.parent_deposit_override_pct || 0)) {
        payload.parent_deposit_override_pct = Number(form.parent_deposit_override_pct);
      }
      if (Number(form.parent_withdraw_override_pct) !== Number(agent.parent_withdraw_override_pct || 0)) {
        payload.parent_withdraw_override_pct = Number(form.parent_withdraw_override_pct);
      }
    }
    const balanceDelta = Number(form.new_balance) - Number(agent.balance || 0);
    setBusy(true);
    await onSubmit(agent, payload, balanceDelta, form.balance_note);
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-md p-6 w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="edit-agent-modal">
        <h3 className="text-lg font-bold mb-1">Edit Agent — <span className="font-mono text-emerald-700">{agent.username}</span></h3>
        <p className="text-xs text-gray-500 mb-5">Update any field below. Changes take effect immediately. Balance changes are logged in the audit trail.</p>

        {/* Identity */}
        <div className="space-y-4">
          <SectionTitle>Identity & Contact</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Username (login)">
              <input value={form.username} onChange={set("username")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm font-mono" data-testid="edit-agent-username" />
            </Field>
            <Field label="Full Name">
              <input value={form.name} onChange={set("name")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="edit-agent-name" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email">
              <input type="email" value={form.email} onChange={set("email")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="edit-agent-email" />
            </Field>
            <Field label="Phone Number">
              <input value={form.mobile} onChange={set("mobile")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="edit-agent-mobile" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="City (Bangladesh district)">
              <select value={form.city} onChange={set("city")} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="edit-agent-city">
                <option value="">— Select district —</option>
                {districts.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="State / Address (unique)">
              <input value={form.state} onChange={set("state")} maxLength={80} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="edit-agent-state" />
            </Field>
          </div>

          {/* Deposit / Balance */}
          <SectionTitle>Deposit Balance</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <Field label={`New Balance (BDT) — current: ৳${Number(agent.balance || 0).toFixed(2)}`}>
              <NumberInput value={form.new_balance} onChange={(n) => setForm({ ...form, new_balance: n })} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular" data-testid="edit-agent-balance" />
            </Field>
            <Field label="Reason / Note (audit log)">
              <input value={form.balance_note} onChange={set("balance_note")} placeholder="e.g. correction, top-up" className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm" data-testid="edit-agent-balance-note" />
            </Field>
          </div>

          {/* Commissions */}
          <SectionTitle>Commissions</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <Pct label="Own · Deposit %" value={form.deposit_commission_pct} onChange={setNum("deposit_commission_pct")} testId="edit-agent-comm-deposit" />
            <Pct label="Own · Withdraw %" value={form.withdraw_commission_pct} onChange={setNum("withdraw_commission_pct")} testId="edit-agent-comm-withdraw" />
          </div>
          {isSub && (
            <div className="border border-purple-200 bg-purple-50/40 rounded-sm p-4">
              <div className="text-[11px] uppercase tracking-wider text-purple-700 font-bold mb-2">
                Parent Override · paid to <span className="font-mono">{agent.parent_agent_username}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Pct label="Parent · Deposit Override %" value={form.parent_deposit_override_pct} onChange={setNum("parent_deposit_override_pct")} testId="edit-agent-parent-deposit" />
                <Pct label="Parent · Withdraw Override %" value={form.parent_withdraw_override_pct} onChange={setNum("parent_withdraw_override_pct")} testId="edit-agent-parent-withdraw" />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="h-9 px-4 border border-gray-200 rounded-sm text-sm" data-testid="edit-agent-cancel">Cancel</button>
          <button disabled={busy} onClick={submit} className="h-9 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold disabled:opacity-50" data-testid="edit-agent-save">
            {busy ? "Saving..." : "Save All Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold border-b border-gray-100 pb-1 pt-2">{children}</div>;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</label>
      {children}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────
// Commission Credit Modal (monthly 3% performance commission)
// ─────────────────────────────────────────────────────────────────────
function CommissionCreditModal({ agent, onClose, onSubmit }) {
  // default period = previous calendar month (e.g. "May 2026") since
  // commissions are usually credited on the 1st for the prior month
  const defaultPeriod = (() => {
    const d = new Date();
    d.setDate(0); // step back to last day of previous month
    return d.toLocaleString("en-US", { month: "long", year: "numeric" });
  })();

  const [period, setPeriod] = useState(defaultPeriod);
  const [turnover, setTurnover] = useState("");
  const [pct, setPct] = useState("3");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [manualAmount, setManualAmount] = useState(false);

  const numTurnover = parseFloat(turnover) || 0;
  const numPct = parseFloat(pct) || 0;
  const auto = (numTurnover * numPct) / 100;
  const finalAmount = manualAmount ? parseFloat(amount) || 0 : auto;
  const isValid = finalAmount > 0 && period.trim().length > 0;
  const prevBal = Number(agent.balance || 0);
  const nextBal = prevBal + finalAmount;

  const submit = async () => {
    setBusy(true);
    await onSubmit(agent, {
      period_label: period.trim(),
      monthly_turnover: numTurnover,
      commission_pct: numPct,
      amount: finalAmount,
      note: note.trim(),
    });
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="commission-credit-modal-backdrop"
    >
      <div
        className="bg-white rounded-lg w-full max-w-lg overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="commission-credit-modal"
      >
        {/* Header — emerald + champagne, signals "treasury / payout" */}
        <div
          className="px-6 pt-5 pb-4 text-white"
          style={{
            background:
              "linear-gradient(135deg,#021307 0%,#04210f 45%,#0f5132 100%)",
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="h-10 w-10 rounded-md grid place-items-center"
              style={{
                background: "rgba(212,168,42,0.18)",
                border: "1px solid #d4a82a",
              }}
            >
              <Coins size={18} color="#d4a82a" />
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="text-[10.5px] font-bold tracking-[3px] uppercase"
                style={{ color: "#d4a82a" }}
              >
                Performance Commission · Monthly Credit
              </div>
              <div className="text-lg font-bold leading-tight truncate">
                {agent.name || agent.username}
                <span className="ml-2 font-mono text-[12px] opacity-80">
                  @{agent.username}
                </span>
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between text-[11px] opacity-90">
            <span>
              Current balance{" "}
              <b className="text-[#fef3c7]">৳ {prevBal.toFixed(2)}</b>
            </span>
            <span>{agent.email}</span>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                Settlement Period
              </label>
              <input
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                placeholder="e.g. May 2026"
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
                data-testid="commission-period"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                Commission Rate (%)
              </label>
              <NumberInput
                value={pct}
                onChange={(raw) => setPct(raw)}
                asString
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
                data-testid="commission-pct"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
              Monthly Turnover (৳){" "}
              <span className="text-gray-400 normal-case tracking-normal">
                — used to auto-calculate commission
              </span>
            </label>
            <NumberInput
              value={turnover}
              onChange={(raw) => setTurnover(raw)}
              asString
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
              data-testid="commission-turnover"
              placeholder="e.g. 66666.67"
            />
          </div>

          {/* Computed preview card */}
          <div
            className="rounded-md border p-4"
            style={{
              background: "linear-gradient(135deg,#fefbe7 0%,#fff 100%)",
              borderColor: "#e8dec0",
            }}
          >
            <div className="flex items-center justify-between text-[10.5px] font-bold uppercase tracking-[2px] text-amber-700 mb-2">
              <span>Auto-Computed Commission</span>
              <span>{numPct.toFixed(2)}% of ৳{numTurnover.toFixed(2)}</span>
            </div>
            <div className="flex items-end justify-between">
              <div className="text-3xl font-bold text-emerald-900 tabular">
                ৳ {auto.toFixed(2)}
              </div>
              <button
                type="button"
                onClick={() => {
                  setManualAmount((v) => !v);
                  if (!manualAmount) setAmount(auto.toFixed(2));
                }}
                className="text-[11px] underline text-emerald-700 hover:text-emerald-900"
                data-testid="commission-toggle-manual"
              >
                {manualAmount ? "Use auto amount" : "Override manually"}
              </button>
            </div>
            {manualAmount && (
              <div className="mt-3">
                <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                  Manual Amount (৳)
                </label>
                <NumberInput
                  value={amount}
                  onChange={(raw) => setAmount(raw)}
                  asString
                  className="w-full h-10 px-3 border border-amber-300 rounded-sm text-sm tabular bg-white"
                  data-testid="commission-manual-amount"
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
              Note (optional · appears in email)
            </label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. 3% of total successful turnover for May 2026 · auto-audited."
              className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm"
              data-testid="commission-note"
            />
          </div>

          {/* Final settlement summary */}
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs grid grid-cols-3 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                Previous Balance
              </div>
              <div className="font-mono text-gray-800 mt-0.5">
                ৳ {prevBal.toFixed(2)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">
                + Credit
              </div>
              <div className="font-mono font-bold text-amber-700 mt-0.5">
                ৳ {finalAmount.toFixed(2)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
                New Balance
              </div>
              <div className="font-mono font-bold text-emerald-700 mt-0.5">
                ৳ {nextBal.toFixed(2)}
              </div>
            </div>
          </div>

          <div className="text-[11px] text-gray-500 flex items-start gap-1.5">
            <Mail size={12} className="mt-0.5 flex-shrink-0 text-emerald-600" />
            <span>
              Submitting will <b>credit the wallet</b> and immediately send an{" "}
              <b>ultra-professional &ldquo;Commission Credited&rdquo; email</b> from{" "}
              <code className="bg-gray-100 px-1 rounded">agent@maxlive88.com</code>{" "}
              (Agent Email Gateway).
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="h-9 px-4 border border-gray-300 rounded-sm text-sm text-gray-700 hover:bg-white"
            data-testid="commission-cancel"
          >
            Cancel
          </button>
          <button
            disabled={busy || !isValid}
            onClick={submit}
            className="h-9 px-5 rounded-sm text-sm font-semibold text-white disabled:opacity-50"
            style={{
              background: busy
                ? "#6b7280"
                : "linear-gradient(135deg,#0f5132 0%,#04210f 100%)",
              boxShadow: "0 2px 6px rgba(15,81,50,0.35)",
            }}
            data-testid="commission-submit"
          >
            {busy
              ? "Crediting..."
              : `Credit ৳ ${finalAmount.toFixed(2)} & Email Agent`}
          </button>
        </div>
      </div>
    </div>
  );
}
